import React, { useState, useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import axiosInstance from "../../utils/axiosInstance";
import { API_ENDPOINTS } from "../../constants/config";
import AddSchoolModal from "./AddSchoolModal";
import SchoolList from "./SchoolList";
import { PlusCircle, Search, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const VALID_TENANT_TYPES = ["all", "school", "industry"];

function SchoolsManagement() {
  const [searchParams] = useSearchParams();
  const initialTenantType = searchParams.get("type");
  const [schools, setSchools] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [error, setError] = useState("");
  const [tenantTypeFilter, setTenantTypeFilter] = useState(
    VALID_TENANT_TYPES.includes(initialTenantType) ? initialTenantType : "all"
  );
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");

  useEffect(() => {
    fetchSchoolStats();
  }, []);

  const fetchSchools = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axiosInstance.get(API_ENDPOINTS.schools, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSchools(response.data.data.schools);
    } catch (error) {
      setError("Failed to fetch customers");
    }
  };

  const fetchSchoolStats = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axiosInstance.get(API_ENDPOINTS.schoolsStats, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSchools(response.data.data.schools);
    } catch (error) {
      fetchSchools();
    }
  };

  const handleDeleteSchool = async (schoolId) => {
    setError("");
    if (
      !window.confirm(
        "Are you sure you want to delete this customer? All associated users will be deleted."
      )
    ) {
      return;
    }

    try {
      const id = typeof schoolId === "string" ? schoolId : schoolId?.toString?.() ?? schoolId;
      await axiosInstance.delete(API_ENDPOINTS.school(id), {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      setError("");
      fetchSchoolStats();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to delete customer");
    }
  };

  const handleSettingsUpdate = async (updatedSchools) => {
    setSchools(updatedSchools);
  };

  const user = (() => {
    try {
      return JSON.parse(localStorage.getItem("user") || "{}");
    } catch {
      return {};
    }
  })();
  const isSuperSchoolAdmin = user?.role === "superchooladmin";

  const filteredSchools = useMemo(() => {
    const query = search.trim().toLowerCase();
    return schools.filter((s) => {
      const type = (s?.tenantType || "school").toLowerCase();
      if (tenantTypeFilter !== "all" && type !== tenantTypeFilter) return false;

      const isActive = s.isActive !== false;
      if (statusFilter === "active" && !isActive) return false;
      if (statusFilter === "inactive" && isActive) return false;

      if (query) {
        const haystack = `${s.name || ""} ${s.domain || ""}`.toLowerCase();
        if (!haystack.includes(query)) return false;
      }
      return true;
    });
  }, [schools, tenantTypeFilter, statusFilter, search]);

  const hasActiveFilters = tenantTypeFilter !== "all" || statusFilter !== "all" || search.trim() !== "";
  const clearFilters = () => {
    setTenantTypeFilter("all");
    setStatusFilter("all");
    setSearch("");
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center flex-wrap gap-2">
        <div>
          <h2 className="text-2xl font-bold">Manage Customers</h2>
          <p className="text-sm text-muted-foreground mt-0.5">
            {hasActiveFilters
              ? `Showing ${filteredSchools.length} of ${schools.length} customer(s)${isSuperSchoolAdmin ? " in your group" : ""}.`
              : `Showing ${schools.length} customer(s)${isSuperSchoolAdmin ? " in your group" : ""}.`}
          </p>
        </div>
        {!isSuperSchoolAdmin && (
          <button
            onClick={() => setIsModalOpen(true)}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg font-semibold hover:bg-blue-700 transition-colors flex items-center"
          >
            <PlusCircle className="h-5 w-5 mr-2" />
            Add Customer
          </button>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2 rounded-2xl border border-gray-100 bg-white p-3 shadow-sm">
        <div className="relative min-w-[220px] flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name or domain..."
            className="pl-9"
          />
        </div>
        <Select value={tenantTypeFilter} onValueChange={setTenantTypeFilter}>
          <SelectTrigger className="w-[140px]">
            <SelectValue placeholder="Type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            <SelectItem value="school">School</SelectItem>
            <SelectItem value="industry">Industry</SelectItem>
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[140px]">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="inactive">Inactive</SelectItem>
          </SelectContent>
        </Select>
        {hasActiveFilters && (
          <button
            type="button"
            onClick={clearFilters}
            className="inline-flex items-center gap-1 rounded-lg px-3 py-2 text-sm text-gray-500 transition-colors hover:bg-gray-50 hover:text-gray-700"
          >
            <X className="h-3.5 w-3.5" />
            Clear
          </button>
        )}
      </div>

      {error && (
        <div
          className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative"
          role="alert"
        >
          {error}
        </div>
      )}

      <SchoolList 
        schools={filteredSchools} 
        onDeleteSchool={handleDeleteSchool} 
        onSettingsUpdate={handleSettingsUpdate}
      />

      <AddSchoolModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSchoolAdded={fetchSchoolStats}
      />
    </div>
  );
}

export default SchoolsManagement;

